import { isSsoReturnError, SSO_RETURN_ERRORS } from '@fluxus/shared';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router';
import { AuthScreen } from '@/components/layout/AuthScreen';
import { FormAlert } from '@/components/ui/form';
import { LoadingState } from '@/components/states/States';
import { TwoFactorSetupStep } from '@/features/auth/TwoFactorSetupStep';
import { TwoFactorStep } from '@/features/auth/TwoFactorStep';
import { takeSsoPending } from '@/features/auth/sso';
import { errorMessage } from '@/lib/api/errors';
import { homePathFor } from '@/lib/auth/access';
import { useAuth } from '@/lib/auth/session';
import { SessionLoadingPage } from './AccessPages';

type Step =
  | { kind: 'working' }
  | { kind: 'error'; message: string }
  | { kind: 'two-factor'; token: string; step: 'verify' | 'setup'; email: string };

/** Lê e tira do endereço o retorno do SSO (#codigo= ou #erro=): o código não fica no histórico. */
function readReturn(): { code: string | null; error: string | null } {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ''));
  if (window.location.hash) {
    window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search);
  }
  return { code: params.get('codigo'), error: params.get('erro') };
}

/**
 * Volta do login pelo SSO (T-028d, RF-04; DEC-073): troca o código de uso único pela sessão com o verificador guardado
 * nesta aba no início, ou mostra por que a entrada foi recusada.
 */
export function SsoReturnPage() {
  const { state, client } = useAuth();
  const [step, setStep] = useState<Step>({ kind: 'working' });
  // Uma troca só (o código vale uma vez), mesmo com o efeito repetido em desenvolvimento.
  const started = useRef(false);

  useEffect(() => {
    // Espera a restauração da sessão terminar: a renovação recusada não pode apagar a sessão recém-aberta.
    if (state.status === 'loading' || started.current) return;
    started.current = true;
    const { code, error } = readReturn();
    const pending = takeSsoPending();
    if (error || !code) {
      setStep({
        kind: 'error',
        message:
          error && isSsoReturnError(error) ? SSO_RETURN_ERRORS[error] : SSO_RETURN_ERRORS.LOGIN_EXPIRED,
      });
      return;
    }
    if (!pending) {
      setStep({
        kind: 'error',
        message:
          'Esta entrada pelo SSO começou em outra aba ou outro navegador. Volte ao login e entre com SSO por aqui.',
      });
      return;
    }
    client
      .completeSso(code, pending.verifier)
      .then((outcome) => {
        // Sessão aberta: a tela redireciona assim que o estado muda.
        if (outcome.kind === 'two-factor') {
          setStep({
            kind: 'two-factor',
            token: outcome.challengeToken,
            step: outcome.step,
            // A conta com que a pessoa entrou no provedor (pode não ser o e-mail digitado no início).
            email: outcome.email ?? pending.email,
          });
        }
      })
      .catch((err: unknown) => setStep({ kind: 'error', message: errorMessage(err) }));
  }, [client, state.status]);

  if (state.status === 'authenticated') return <Navigate to={homePathFor(state.session.user)} replace />;
  if (state.status === 'loading' && step.kind === 'working') return <SessionLoadingPage />;

  const backToLogin = (
    <Link
      to="/entrar"
      className="mt-6 inline-flex text-sm font-semibold text-brand-strong underline-offset-4 hover:underline dark:text-brand"
    >
      Voltar para o login
    </Link>
  );

  return (
    <AuthScreen>
      {step.kind === 'two-factor' ? (
        step.step === 'setup' ? (
          <TwoFactorSetupStep
            challengeToken={step.token}
            email={step.email}
            onRestart={(reason) => setStep({ kind: 'error', message: errorMessage(reason ?? null) })}
          />
        ) : (
          <TwoFactorStep
            challengeToken={step.token}
            onRestart={(reason) =>
              setStep({
                kind: 'error',
                message: reason ? errorMessage(reason) : 'Comece a entrada pelo SSO de novo.',
              })
            }
          />
        )
      ) : step.kind === 'error' ? (
        <>
          <h1 className="text-3xl font-extrabold tracking-tight">Não foi possível entrar</h1>
          <div className="mt-6">
            <FormAlert tone="error" title="Entrada pelo SSO recusada">
              {step.message}
            </FormAlert>
          </div>
          {backToLogin}
        </>
      ) : (
        <>
          <h1 className="text-3xl font-extrabold tracking-tight">Entrando…</h1>
          <LoadingState rows={2} label="Concluindo a entrada pelo SSO" className="mt-6" />
        </>
      )}
    </AuthScreen>
  );
}
